/**
 * One coach mark: the highlighted element and the card that explains it.
 *
 * The card is portalled into the topmost open <dialog> when there is one:
 * a modal sheet (the report form) makes everything outside it inert, so a
 * card on the page would be visible but not clickable. Inside the sheet it
 * sits at the bottom of the frame, in the sheet's own layer.
 *
 * Anchors are found by polling rather than by a ref: they live in screens
 * that know nothing about the tour, appear after data loads, and some only
 * after the person did something (tap the drawing).
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { resolveHelpText, tx, type TourStep } from '@fsv/data';
import { Icon } from '../components/Icon';
import { demoHomePath } from '../lib/demoEntry';
import './tour.css';

const POLL_MS = 300;

/** The element for an anchor: a visible one if several (rail and tab bar both carry nav anchors). */
export function findAnchor(anchor: string): HTMLElement | null {
  const all = [...document.querySelectorAll<HTMLElement>(`[data-tour="${anchor}"]`)];
  return all.find((el) => el.getClientRects().length > 0) ?? all[0] ?? null;
}

function topContainer(): HTMLElement {
  const dialogs = [...document.querySelectorAll<HTMLDialogElement>('dialog[open]')];
  const top = dialogs.at(-1);
  return (top?.querySelector<HTMLElement>('.sheet__frame') ?? top) || document.body;
}

export function TourOverlay({
  step,
  index,
  count,
  autoOpen,
  onBack,
  onNext,
  onPause,
  onEnd,
}: {
  step: TourStep;
  index: number;
  count: number;
  /** The person moved to this step (Weiter, Zurück, start): open its page if the element is elsewhere. */
  autoOpen: boolean;
  onBack: () => void;
  onNext: () => void;
  onPause: () => void;
  onEnd: () => void;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [found, setFound] = useState(false);
  const [container, setContainer] = useState<HTMLElement>(() => topContainer());
  const navigated = useRef(false);
  const advanced = useRef(false);
  const nextRef = useRef(onNext);
  nextRef.current = onNext;
  const last = index === count - 1;

  const target = step.webPath === undefined ? null : `${demoHomePath().replace(/\/$/, '')}${step.webPath}`;

  // Open the step's page once, when the person moved here and its element is elsewhere.
  useEffect(() => {
    if (navigated.current || !autoOpen || target === null) return;
    navigated.current = true;
    if (findAnchor(step.anchor)) return;
    if (location.pathname !== target) navigate(target);
  }, [step, autoOpen, target, navigate, location.pathname]);

  // Highlight the element while the step is shown; move on when the person did the action.
  useEffect(() => {
    let current: HTMLElement | null = null;
    const tick = () => {
      const el = findAnchor(step.anchor);
      if (el !== current) {
        current?.classList.remove('tour-target');
        el?.classList.add('tour-target');
        el?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
        current = el;
      }
      setFound(el !== null);
      setContainer((c) => {
        const next = topContainer();
        return next === c ? c : next;
      });
      if (step.advanceOn && !advanced.current && findAnchor(step.advanceOn)) {
        advanced.current = true;
        nextRef.current();
      }
    };
    tick();
    const timer = window.setInterval(tick, POLL_MS);
    return () => {
      window.clearInterval(timer);
      current?.classList.remove('tour-target');
    };
  }, [step]);

  const inSheet = container !== document.body;

  return createPortal(
    <section
      className={`tour-card${inSheet ? ' tour-card--inline' : ''}`}
      role="dialog"
      aria-modal="false"
      aria-labelledby="tour-card-title"
      data-testid="tour-card"
    >
      <header className="tour-card__head">
        <span className="tour-card__kicker">
          <Icon name="flag" size={16} />
          {tx.tour.label} · {tx.tour.position(index + 1, count)}
        </span>
        <button
          type="button"
          className="btn btn--ghost btn--icon"
          onClick={onPause}
          aria-label={tx.tour.pause}
          title={tx.tour.pause}
        >
          <Icon name="close" size={18} />
        </button>
      </header>
      <h2 id="tour-card-title" className="tour-card__title">
        {resolveHelpText(step.title)}
      </h2>
      <p className="tour-card__body">{resolveHelpText(step.body)}</p>
      {step.advanceOn && <p className="tour-card__hint">{tx.tour.waiting}</p>}
      {!found && !step.advanceOn && (
        <p className="tour-card__hint">
          {tx.tour.notVisible}{' '}
          {target !== null && (
            <button type="button" className="link-btn" onClick={() => navigate(target)}>
              {tx.tour.show}
            </button>
          )}
        </p>
      )}
      <div className="tour-card__actions">
        <button type="button" className="btn btn--ghost tour-card__end" onClick={onEnd}>
          {tx.tour.skip}
        </button>
        <span className="spacer" />
        {index > 0 && (
          <button type="button" className="btn" onClick={onBack}>
            {tx.tour.back}
          </button>
        )}
        <button type="button" className="btn btn--primary" onClick={onNext}>
          {last ? tx.tour.done : tx.tour.next}
        </button>
      </div>
    </section>,
    container,
  );
}
